/*
  XMARLIN — product page behaviour.

  No framework, no dependencies, one file. Jobs:

    1. option selection -> resolve the variant, update price, struck price,
       savings, hero image, hidden variant id and the URL
    2. thumbnail clicks -> swap the hero image
    3. pack tier -> show one configurator per pack, resolve each pack's own
       variant, and post them as separate cart line items
    4. dispatch countdown -> run to a real daily cut-off hour
    5. cross-sell -> add to cart over the Ajax API without leaving the page
    6. add-to-cart vs buy-now -> same line items, different destination
    7. reveal the sticky console once the main CTA has scrolled out of view

  Jobs 3-5 arrived 2026-09-22 with the "PDP Tech Minimal" design (Stitch node
  c0bb69a0f290…). This file's header used to say countdowns and stock counters
  were "deliberately absent" under shopify/COPY-CONTRACT.md §3; the owner
  lifted that restriction (shopify/DECISIONS.md D31), so the countdown is
  here. The stock-pressure bar needs no script — it is a static width.

  It still drives BOTH option pickers — snippets/xm-options.liquid (dots) and
  snippets/xm-tech-options.liquid (photo tiles) — because they share one
  contract: an <xm-options> (or Dawn's <variant-selects>) of radios plus a
  [data-xm-variants] JSON
  blob. Selectors below are unions for the same reason: the homepage
  Compositeur renders the old markup and the PDP renders the new.
*/
(function () {
  'use strict';

  // No client-side money formatting, ever. Liquid renders every figure this
  // file might need — per variant and per tier — already formatted, and the
  // script only swaps finished strings in. Formatting here is what made the
  // first build print "499.00 د.م." on load and "499 dh" after a click.

  // Resolve after the next paint: rAF then a 0 ms timeout, with a hard
  // fallback so a hidden tab (no rAF) never leaves work pending. Work queued
  // behind this runs after the tap's visual update, outside its INP.
  const yieldToPaint = () =>
    new Promise((resolve) => {
      let done = false;
      const fin = () => {
        if (done) return;
        done = true;
        resolve();
      };
      window.requestAnimationFrame(() => window.setTimeout(fin, 0));
      window.setTimeout(fin, 120);
    });

  // A Shopify image URL at another width (same file, other rendition).
  const atWidth = (src, w) => String(src).replace(/([?&]width=)\d+/, (m, k) => k + w);
  const hasWidth = (src) => /[?&]width=\d+/.test(String(src));

  const show = (el, on) => {
    if (!el) return;
    if (on) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  };

  class XmProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form[action*="/cart/add"]');
      this.idInput = root.querySelector('[data-xm-variant-id]');
      this.qtyInput = root.querySelector('[data-xm-quantity]');
      this.priceEl = root.querySelector('[data-xm-price]');
      this.wasEl = root.querySelector('[data-xm-was]');
      this.savingEl = root.querySelector('[data-xm-saving]');
      this.savingTextEl = root.querySelector('[data-xm-saving-text]');
      this.heroSavingEl = root.querySelector('[data-xm-hero-saving]');
      this.stickyPriceEl = root.querySelector('[data-xm-sticky-price]');
      this.consoleWasEl = root.querySelector('[data-xm-console-was]');
      this.consoleSavingEl = root.querySelector('[data-xm-console-saving]');
      this.compositionEl = root.querySelector('[data-xm-composition]');
      this.stockWrap = root.querySelector('[data-xm-stock-wrap]');
      this.stockText = root.querySelector('[data-xm-stock-text]');
      this.heroImg = root.querySelector('.xm-hero-frame img, .tpdp-hero img');
      this.addBtn = root.querySelector('[data-xm-add]');
      this.addText = root.querySelector('[data-xm-add-text]');
      this.formErrEl = root.querySelector('.tpdp-form__err');
      this.nameEl = root.querySelector('[data-xm-field="name"]');
      this.telEl = root.querySelector('[data-xm-field="tel"]');
      this.cityEl = root.querySelector('[data-xm-field="city"]');
      // The PDP picker is <xm-options>; the homepage Compositeur still
      // renders Dawn's <variant-selects>. Accept either.
      this.selects = root.querySelector('variant-selects, xm-options');
      this.sticky = root.querySelector('[data-xm-sticky]');

      // Quick-order variant only (section setting `quick_order`) — absent
      // on the default template, so every one of these is null there and
      // bindSticky()/bindSheet() take their existing, unchanged branch.
      // `this.quick` is read off the root, not off the sticky console:
      // settings.xm_sticky_bar can be off while quick_order is still on,
      // and the console's own presence was never the right thing to gate
      // the colour/label trims below on.
      this.quick = root.dataset.xmQuick === 'true';
      // Round 5: the D59 garment-claims gate (show_garment_claims in
      // Liquid) — accessories never get a pack discount, a badge or a
      // struck pack price. Read here for the one struck price this file
      // adds client-side (paintPacks()'s per-pack readback); everything
      // else that gate controls is decided in Liquid already.
      this.showClaims = root.dataset.xmClaims === 'true';
      this.sheet = root.querySelector('[data-xm-sheet]');
      this.sheetBody = root.querySelector('[data-xm-sheet-body]');
      this.sheetTitle = root.querySelector('[data-xm-sheet-title]');
      this.sheetClose = root.querySelector('[data-xm-sheet-close]');
      this.sheetTrigger = root.querySelector('[data-xm-sheet-trigger]');
      this.configSection = root.querySelector('[id^="xm-config-"]');
      this.orderFormSection = root.querySelector('[id^="xm-order-form-"]');
      this.xsellModal = root.querySelector('[data-xm-xsell-modal]');
      this.xsellRailSection = root.querySelector('[id^="xm-xsell-"]');
      this.xsellModalShown = false;
      this.submitLock = false;

      this.packWrap = root.querySelector('.tpdp-packs');
      this.packCards = Array.from(root.querySelectorAll('[data-xm-pack-card]'));
      this.cartBtn = root.querySelector('[data-xm-intent="cart"]');
      this.cartText = root.querySelector('[data-xm-cart-text]');

      this.tierPills = Array.from(root.querySelectorAll('[data-xm-tier]'));
      this.tierTotalEl = root.querySelector('[data-xm-tier-total]');
      this.tierWasEl = root.querySelector('[data-xm-tier-was]');
      this.tierTitleEl = root.querySelector('[data-xm-tier-title]');
      this.tierSavingLineEl = root.querySelector('[data-xm-tier-saving-line]');

      this.variants = this.readJson('[data-xm-variants]');
      this.tierTotals = this.readJson('[data-xm-tier-totals]');
      this.tier = this.currentTier();

      this.bindOptions();
      this.bindSubmit();
      this.bindLivraSuccess();
      this.bindThumbs();
      this.bindSizeGuide();
      this.bindLightbox();
      this.bindSheet();
      // Before bindTiers(): that call paints the tier total for the first
      // time, and paintTier() -> paintXsellModalTotal() needs
      // this.xsellModalConfirm (set inside bindXsellModal()) already in
      // place to update the modal's button rather than leave it on its
      // server-rendered initial text for one extra paint.
      this.bindXsellModal();
      this.bindTiers();
      // bindTiers returns early when the tier ladder is off, so the pack
      // cards would never get their first paint. They still need one: the
      // readback line and the disabled state of the hidden packs' radios are
      // set here, not in Liquid.
      this.paintPacks();
      // The stock band ships hidden and was only painted on an option change,
      // so a preselected size never showed its count. No-op without the band.
      this.paintStock(this.matchVariant());
      // Not needed for first paint, the colour tiles, the hero, the size guide
      // or the COD form: cross-sell cards, the sticky bar's observer, the
      // dispatch clock and the FAQ link check. They run when the browser is
      // idle, or at once on the first pointer, key, touch or scroll, so a
      // first tap on a cross-sell card is never lost (pointerdown fires
      // before click). Everything the submit path reads from them is
      // guarded (this.xsell may be unset: xsellCents/activeXsell/clearXsell
      // check it), and Livra finds its button and variant input from the
      // constructor above, not from these.
      // After load and an idle slot: warm the other colourways' hero (800 w).
      if (typeof window.xmWhenIdle === 'function') window.xmWhenIdle(() => this.preloadColourHeroes());
      this.runDeferred(() => {
        this.bindCrossSell();
        this.bindSticky();
        this.bindCountdown();
        this.checkFaqLink();
      });

      if (this.quick) {
        // Round 3: trims the colour row's own label text and gives every
        // colour tile's caption a home on aria-label before the section's
        // own CSS hides that caption — both one-time passes over markup
        // that does not change shape afterwards (the radios stay the same
        // nodes; only the tier pills show/hide whole pack cards, which
        // paintPacks() above already repaints on its own).
        this.trimQuickColourLabels();
        this.paintQuickTileNames();
      }
    }

    /* ---------------------------------------------- quick-order trims (round 3) */

    /*
      "Coloris (sélection par photo)" -> "Coloris". The suffix is baked into
      snippets/xm-tech-options.liquid's own label text (shared with the
      default page, not in this round's touch list), so it is trimmed here
      rather than never rendered. Scoped to `.tpdp-config .xmt-opts__row:not(.xmt-opts__row--chips)`
      — the colour row, never the size row, which never had the suffix — and
      NOT scoped to one pack card: tier 2/3 render one `<xm-options>` per
      pack, and every one of them gets the same trim.
    */
    // Run `fn` once: on idle (setTimeout fallback), or on the first
    // interaction, whichever comes first.
    runDeferred(fn) {
      let done = false;
      const evts = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
      const run = () => {
        if (done) return;
        done = true;
        evts.forEach((e) => window.removeEventListener(e, soon, true));
        try { fn(); } catch (err) { /* a deferred extra must never break the page */ }
      };
      // On an interaction, run after the next paint, never inside the input
      // handler: work done there is counted in that tap's INP (600 ms in
      // Clarity, 2026-10-06).
      const soon = () => window.requestAnimationFrame(() => window.setTimeout(run, 0));
      evts.forEach((e) => window.addEventListener(e, soon, { capture: true, passive: true }));
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
      else window.setTimeout(run, 1200);
    }

    trimQuickColourLabels() {
      this.root
        .querySelectorAll(
          '.tpdp-config .xmt-opts__row:not(.xmt-opts__row--chips) .xmt-opts__head > span:first-child'
        )
        .forEach((el) => {
          const trimmed = el.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (trimmed) el.textContent = trimmed;
        });
    }

    // Gives each colour tile's accessible name a permanent home on its own
    // <label> (aria-label), read once before the section's CSS hides the
    // caption (`.xmt-tile__name { display: none }`) that used to carry it.
    paintQuickTileNames() {
      this.root
        .querySelectorAll('.tpdp-config .xmt-opts__row:not(.xmt-opts__row--chips) .xmt-tile')
        .forEach((label) => {
          if (label.hasAttribute('aria-label')) return;
          const nameEl = label.querySelector('.xmt-tile__name');
          const name = nameEl ? nameEl.textContent.trim() : '';
          if (name) label.setAttribute('aria-label', name);
        });
    }

    /*
      The description accordion links down to the FAQ section. Liquid derives
      that id from this section's own — both are `template--<n>__<key>` — so
      it is right whenever the FAQ section is still in the template under the
      key the setting names. When it is not (section removed, key renamed),
      the href points at nothing and the link silently does nothing when
      clicked, which is worse than not offering it.
    */
    checkFaqLink() {
      const link = this.root.querySelector('[data-xm-faq-link]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href.length > 1 && document.getElementById(href.slice(1))) return;
      // Last chance before hiding it: any FAQ section on this page will do.
      const any = document.querySelector('[id^="xm-faq-"]');
      if (any) link.setAttribute('href', '#' + any.id);
      else link.hidden = true;
    }

    readJson(selector) {
      const el = this.root.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    currentTier() {
      const pressed = this.tierPills.find((p) => p.getAttribute('aria-pressed') === 'true');
      return pressed ? parseInt(pressed.dataset.xmTier, 10) : 1;
    }

    // The configurator for pack n. Pack 1 is also the plain single-pack
    // picker, so `scope(1)` is the whole page's selection when no tier is on.
    scope(n) {
      return (
        this.root.querySelector(
          '[data-xm-pack-card="' + n + '"] variant-selects, ' +
          '[data-xm-pack-card="' + n + '"] xm-options'
        ) ||
        this.selects
      );
    }

    selectedOptions(n) {
      const el = this.scope(n || 1);
      if (!el) return [];
      return Array.from(el.querySelectorAll('input[type="radio"]:checked')).map((i) => i.value);
    }

    matchVariant(n) {
      if (!this.variants) return null;
      const chosen = this.selectedOptions(n || 1);
      if (!chosen.length) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
    }

    // Every pack in play, in order. A pack whose combination the shop does
    // not carry resolves to null and is reported, never silently dropped —
    // dropping it would quietly charge for fewer items than the tier said.
    activePacks() {
      const count = this.packCards.length ? this.tier : 1;
      const out = [];
      for (let n = 1; n <= count; n += 1) out.push(this.matchVariant(n));
      return out;
    }

    bindOptions() {
      // Delegated: there are up to three <xm-options> and they are shown
      // and hidden as the tier changes, so binding each one individually
      // would miss any that were hidden at construction.
      this.root.addEventListener('change', (e) => {
        if (!e.target || e.target.type !== 'radio') return;
        if (!e.target.closest('variant-selects, xm-options')) return;
        this.update();
      });

      // A colour tile is a label around a radio. Tapping the one already
      // chosen fires no change, so nothing moved on screen (Clarity: dead
      // taps on span.xmt-tile__frame > img). The tile is a picture of a
      // colourway, so the answer is that colourway in the hero, or, when the
      // hero already shows it, the full-screen view. Pack 1's row only: the
      // rail follows pack 1's colour (pickColour), not the other packs'.
      this.root.addEventListener('click', (e) => {
        if (!e.target || e.target.tagName === 'INPUT') return;
        const tile = e.target.closest && e.target.closest('.xmt-tile');
        if (!tile || !tile.closest('xm-options:not([data-xm-pack]), xm-options[data-xm-pack="1"]')) return;
        const radio = tile.querySelector('input[type="radio"]');
        if (!radio || !radio.checked || !this.thumbs) return;
        const railTile = this.thumbs.find((b) => b.dataset.xmColour === radio.value);
        if (!railTile) return;
        if (railTile.getAttribute('aria-current') === 'true' && this.openLightbox) this.openLightbox();
        else this.showThumb(railTile);
      });
    }

    // The stock line. It says a number only when that number means
    // something: Shopify has to be tracking the variant, the policy has to be
    // `deny` (under `continue` the variant sells past zero and the count runs
    // negative), the count has to be a real positive number at or under the
    // merchant's threshold, and the merchant's string has to carry the {n}
    // token. Any one of those missing and the section stays hidden rather
    // than rendering a figure nobody can stand behind.
    paintStock(variant) {
      if (!this.stockWrap || !this.stockText) return;
      const tpl = this.stockWrap.dataset.xmStockTemplate || '';
      const threshold = parseInt(this.stockWrap.dataset.xmStockThreshold, 10);
      const n = variant && typeof variant.inventory === 'number' ? variant.inventory : null;
      const ok = !!(
        variant &&
        variant.inventory_tracked &&
        !variant.inventory_continues &&
        n !== null &&
        n > 0 &&
        threshold > 0 &&
        n <= threshold &&
        tpl.indexOf('{n}') !== -1
      );
      if (ok) {
        // {size}: the last option is the size (Coloris / Taille).
        const size = variant.options && variant.options.length ? variant.options[variant.options.length - 1] : '';
        this.stockText.textContent = tpl.replace('{n}', String(n)).replace('{size}', size);
      }
      this.stockWrap.hidden = !ok;
    }

    // Colour / size change. The visual work runs inside the tap (chosen
    // state, hero picture, price text, hidden variant id); everything else
    // waits for the next paint (updateRest) and is flushed synchronously by
    // flushUpdate() before an order can be submitted or the sheet opened.
    update() {
      const variant = this.matchVariant();

      if (this.compositionEl) {
        this.compositionEl.textContent = variant
          ? variant.title
          : this.selectedOptions().join(' / ');
      }

      if (!variant) {
        // A combination the shop does not carry. Say so plainly and disable
        // the button rather than letting the customer add something that
        // fails at the cart.
        this.updatePending = false;
        if (this.addBtn) this.addBtn.disabled = true;
        if (this.addText) this.addText.textContent = 'INDISPONIBLE';
        if (this.idInput) this.idInput.disabled = true;
        this.paintStock(null);
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = false;
      }

      if (this.priceEl && variant.price_formatted) {
        this.priceEl.textContent = variant.price_formatted;
      }

      // Hero alt follows the colourway ("<product title> — <colour> / <size>",
      // same shape as the server-rendered alt). The picture swap applies it
      // with the new src; with no picture to swap it is written at once.
      if (this.heroImg) {
        if (this.heroAltPrefix === undefined) {
          const first = this.heroImg.getAttribute('alt') || '';
          const cut = first.indexOf(' \u2014 ');
          this.heroAltPrefix = cut > 0 ? first.slice(0, cut) : '';
        }
        if (this.heroAltPrefix && variant.title) {
          this.heroAltText = this.heroAltPrefix + ' \u2014 ' + variant.title;
          if (!variant.featured_image) this.heroImg.alt = this.heroAltText;
        }
      }

      // showThumb() sets the hero itself, from the tile that was tapped.
      if (variant.featured_image && this.heroImg && !this.heroHandled) {
        this.swapHero(variant.featured_image.src);
      }
      this.syncThumbs(variant);

      this.updatePending = true;
      yieldToPaint().then(() => this.flushUpdate());
    }

    // Put `src` (a Shopify image URL) into the hero with the same
    // srcset/sizes the first hero ships with (800/1200 w, the img's own
    // sizes), so the browser fetches the rendition it already would have, not
    // a fixed 1200 px file. The new picture is decoded off-screen first and
    // swapped in once ready, so the tap never waits on a decode. A later
    // swap supersedes an earlier one still decoding.
    swapHero(src) {
      const img = this.heroImg;
      if (!img || !src) return;
      this.heroToken = (this.heroToken || 0) + 1;
      const token = this.heroToken;
      this.heroPending = src;
      const resized = hasWidth(src);
      const set = resized
        ? [800, 1200].map((w) => atWidth(src, w) + ' ' + w + 'w').join(', ')
        : '';
      const plain = resized ? atWidth(src, 1000) : src;
      const apply = () => {
        if (token !== this.heroToken) return;
        this.heroPending = '';
        if (set) img.srcset = set;
        else img.removeAttribute('srcset');
        img.src = plain;
        if (this.heroAltText) img.alt = this.heroAltText;
      };
      if (typeof Image === 'undefined' || !('decode' in Image.prototype)) {
        apply();
        return;
      }
      const probe = new Image();
      if (set) {
        probe.sizes = img.sizes || img.getAttribute('sizes') || '';
        probe.srcset = set;
      }
      probe.src = plain;
      probe.decode().then(apply, apply);
    }

    // Once the page is idle, fetch the 800 w hero of the other colourways so
    // a colour change swaps a cached picture. Skipped on data-saver.
    preloadColourHeroes() {
      try {
        const conn = navigator.connection;
        if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
        const seen = new Set();
        const urls = [];
        (this.variants || []).forEach((v) => {
          const src = v.featured_image && v.featured_image.src;
          if (!src || !hasWidth(src) || seen.has(src)) return;
          seen.add(src);
          urls.push(atWidth(src, 800));
        });
        const keep = []; // hold references so the loads are not collected
        this.heroPreloads = keep;
        const next = () => {
          const url = urls.shift();
          if (!url) return;
          const im = new Image();
          im.decoding = 'async';
          im.src = url;
          keep.push(im);
          const again = () => idle(next);
          im.onload = again;
          im.onerror = again;
        };
        const idle = (fn) => {
          if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 3000 });
          else window.setTimeout(fn, 300);
        };
        idle(next);
      } catch (e) {
        /* a preload must never break the page */
      }
    }

    // Run the deferred half of update() now, if one is pending. Safe to call
    // at any time and any number of times.
    flushUpdate() {
      if (!this.updatePending) return;
      this.updatePending = false;
      const variant = this.matchVariant();
      if (variant) this.updateRest(variant);
    }

    updateRest(variant) {
      // A multi-pack is only buyable when EVERY pack resolves. Judging the
      // button on pack 1 alone would let someone submit a basket whose
      // second pack is a combination the shop does not carry.
      const allOk = this.activePacks().every((v) => v && v.available);
      if (this.addBtn) this.addBtn.disabled = !allOk;
      if (this.cartBtn) this.cartBtn.disabled = !allOk;
      if (this.addText) {
        this.addText.textContent = allOk
          ? this.addBtn.dataset.labelAvailable || this.addText.textContent
          : 'INDISPONIBLE';
      }

      this.paintStock(variant);

      // Struck price and savings. Null unless the admin set a compare-at
      // price on THIS variant — the theme never invents a former price.
      const was = variant.compare_at_price_formatted;
      const saving = variant.saving_formatted;
      if (this.wasEl) {
        this.wasEl.textContent = was || '';
        show(this.wasEl, !!was);
      }
      if (this.savingEl) {
        if (this.savingTextEl) this.savingTextEl.textContent = saving ? 'ÉCONOMIE : ' + saving : '';
        show(this.savingEl, !!saving);
      }
      if (this.heroSavingEl) {
        this.heroSavingEl.textContent = saving ? '−' + saving : '';
        show(this.heroSavingEl, !!saving);
      }
      if (this.consoleWasEl) {
        this.consoleWasEl.textContent = was || '';
        show(this.consoleWasEl, !!was);
      }
      if (this.consoleSavingEl) {
        this.consoleSavingEl.textContent = saving ? '−' + saving : '';
        show(this.consoleSavingEl, !!saving);
      }

      this.paintTier(variant);

      if (variant.id && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    /* ------------------------------------------------------- pack tiers */

    bindTiers() {
      if (!this.tierPills.length) return;
      this.tierPills.forEach((pill) => {
        pill.addEventListener('click', () => {
          this.tier = parseInt(pill.dataset.xmTier, 10) || 1;
          this.tierPills.forEach((p) =>
            p.setAttribute('aria-pressed', p === pill ? 'true' : 'false')
          );
          if (this.tierTitleEl && pill.dataset.tierHeading) {
            this.tierTitleEl.textContent = pill.dataset.tierHeading;
          }
          this.paintTier(this.matchVariant());
        });
      });
      this.paintTier(this.matchVariant());
    }

    // Show exactly `tier` configurators, and read each one's choice back.
    paintPacks() {
      if (!this.packCards.length) return;
      if (this.packWrap) this.packWrap.dataset.tier = String(this.tier);

      this.packCards.forEach((card) => {
        const n = parseInt(card.dataset.xmPackCard, 10);
        const on = n <= this.tier;
        if (on) card.removeAttribute('hidden');
        else card.setAttribute('hidden', '');

        // A hidden pack must not block the buy button by being "unavailable",
        // and must not be posted. Disabling its radios also keeps them out of
        // a no-JS native submit.
        card.querySelectorAll('input[type="radio"]').forEach((i) => {
          i.disabled = !on;
        });

        const echo = this.root.querySelector('[data-xm-pack-readback="' + n + '"]');
        if (!echo) return;
        if (!on) {
          echo.textContent = '';
          return;
        }
        const v = this.matchVariant(n);
        // The per-pack price is the honest place to show money when packs can
        // differ: it comes straight from Liquid, already formatted.
        echo.textContent = '';
        echo.appendChild(
          document.createTextNode(
            v ? v.title + (v.price_formatted ? ' · ' + v.price_formatted : '') : 'Combinaison indisponible'
          )
        );
        // Round 5 (quick_mode + the D59 garment-claims gate only): the same
        // struck-price idea as the pills and the price card, for this
        // per-unit readback — the garment's own compare-at price, never an
        // invented one, and never on an accessory.
        if (this.quick && this.showClaims && v && v.compare_at_price_formatted) {
          const was = document.createElement('span');
          was.className = 'tpdp-pack__was';
          was.textContent = v.compare_at_price_formatted;
          echo.appendChild(was);
        }
      });
    }

    // Every figure here comes from Liquid: either the per-variant tier map,
    // or — if that is missing — the pill's own rendered dataset.
    paintTier(variant) {
      this.paintPacks();
      if (!this.tierPills.length) return;

      if (this.qtyInput) this.qtyInput.value = String(this.tier);

      const fromMap =
        variant && this.tierTotals && this.tierTotals[variant.id]
          ? this.tierTotals[variant.id][this.tier - 1]
          : null;

      const pill = this.tierPills.find((p) => parseInt(p.dataset.xmTier, 10) === this.tier);
      const total = fromMap ? fromMap.total : pill && pill.dataset.tierTotalFormatted;
      const was = fromMap ? fromMap.was : (pill && pill.dataset.tierWas) || null;
      const saving = fromMap ? fromMap.saving : (pill && pill.dataset.tierSaving) || null;

      /*
        The tier map is `tier x ONE variant's price`, so it is only the right
        answer when every pack resolved to the same price. Packs can now
        differ, and this catalogue is single-price today but Shopify does not
        require that. Rather than format money in JavaScript — the thing that
        printed "499.00 د.م." on load and "499 dh" after a click — a mixed
        basket shows no aggregate and leans on the per-pack lines, each of
        which carries its own Liquid-formatted price.
      */
      const packs = this.activePacks();
      const priced = packs.filter(Boolean);
      const uniform =
        priced.length === packs.length &&
        priced.every((v) => v.price === priced[0].price);

      // Round 4: the cross-sell modal's own CONFIRMER button needs this same
      // pre-withXsell figure, and `data-xm-tier-total` is gone from the DOM
      // in quick_mode (round 3 dropped the card heading it lived in) — so it
      // is kept here rather than re-read from an element that may not exist.
      this.lastBaseTotal = uniform && total ? total : null;
      if (this.quick) {
        this.paintXsellModalTotal();
        this.paintQuickPriceCard(variant);
        this.paintQuickCtaLines();
      }

      if (this.tierTotalEl) {
        this.tierTotalEl.textContent = uniform && total ? this.withXsell(total) : '—';
        this.tierTotalEl.dataset.mixed = uniform ? 'false' : 'true';
      }
      if (this.stickyPriceEl && uniform && total) this.stickyPriceEl.textContent = this.withXsell(total);
      if (this.tierWasEl) {
        this.tierWasEl.textContent = uniform && was ? was : '';
        show(this.tierWasEl, !!(uniform && was));
      }
      if (this.tierSavingLineEl) {
        this.tierSavingLineEl.textContent = uniform && saving ? 'Gain net : ' + saving : '';
      }

      // Keep each pill's own sub-label honest for the current variant.
      if (this.tierTotals && variant && this.tierTotals[variant.id]) {
        this.tierPills.forEach((p) => {
          const idx = parseInt(p.dataset.xmTier, 10) - 1;
          const row = this.tierTotals[variant.id][idx];
          const small = p.querySelector('small');
          // Only rewrite a sub-label that is a PRICE. A tier carrying a word
          // ("ATHLÈTES") is a merchant string and is not ours to overwrite.
          if (row && small && small.dataset.isPrice !== 'false' && /\d/.test(small.textContent)) {
            small.textContent = row.total;
            // quick_order: the pill also carries its struck compare-at x n,
            // rendered server-side into data-tier-was; put it back after the
            // rewrite above, or it is wiped on the first paint.
            if (this.quick && p.dataset.tierWas) {
              const was = document.createElement('span');
              was.className = 'tpdp-pill__was';
              was.textContent = p.dataset.tierWas;
              small.append(' ', was);
            }
          }
        });
      }
    }

    /* ------------------------------------------------- submit: add / buy */

    /*
      The three COD fields, validated. `novalidate` is on the <form> because
      Shopify's native bubble points at whichever field the browser feels
      like and says nothing to a screen reader; this replaces it with a
      message next to the field, aria-invalid on the input, and
      aria-describedby wiring that was already in the markup for exactly
      this. Returns the first invalid field (to focus) or null.

      The phone check also NORMALISES on success, writing the canonical
      +212 form back into the input — that is the string that ends up in
      the "Téléphone" line-item property, not whatever punctuation the
      customer typed.
    */
    validateCodFields() {
      this.clearFieldError(this.nameEl);
      this.clearFieldError(this.telEl);
      this.clearFieldError(this.cityEl);

      if (this.nameEl) {
        const name = this.nameEl.value.trim();
        if (!name) return this.failField(this.nameEl, 'Entrez votre nom complet');
        if (name.length < 3) return this.failField(this.nameEl, 'Nom trop court (3 caractères minimum)');
      }

      if (this.telEl) {
        const raw = this.telEl.value.trim();
        if (!raw) return this.failField(this.telEl, 'Entrez votre numéro de téléphone');
        const canonical = this.normalizePhone(raw);
        if (!canonical) return this.failField(this.telEl, 'Numéro de téléphone invalide');
        this.telEl.value = canonical;
      }

      if (this.cityEl) {
        if (!this.cityEl.value) return this.failField(this.cityEl, 'Choisissez votre ville de livraison');
      }

      return null;
    }

    // Accepts 06…, 07…, +2126…, +2127…, 00212…, with spaces, dots or dashes
    // anywhere — everything that is not a digit is stripped before the
    // prefix is read, so punctuation never matters. Returns the canonical
    // "+2126XXXXXXXX" / "+2127XXXXXXXX" form, or null if what is left is not
    // a plausible Moroccan mobile number.
    normalizePhone(raw) {
      if (!raw) return null;
      const digits = String(raw).replace(/\D/g, '');
      let national;
      if (digits.indexOf('00212') === 0) national = digits.slice(5);
      else if (digits.indexOf('212') === 0) national = digits.slice(3);
      else if (digits.charAt(0) === '0') national = digits.slice(1);
      else national = digits;
      if (!/^[67]\d{8}$/.test(national)) return null;
      return '+212' + national;
    }

    failField(field, message) {
      field.setAttribute('aria-invalid', 'true');
      const wrap = field.closest('.tpdp-tel');
      if (wrap) wrap.dataset.invalid = 'true';
      const err = document.getElementById(field.getAttribute('aria-describedby'));
      if (err) err.textContent = message;
      return field;
    }

    clearFieldError(field) {
      if (!field) return;
      field.setAttribute('aria-invalid', 'false');
      const wrap = field.closest('.tpdp-tel');
      if (wrap) wrap.removeAttribute('data-invalid');
      const err = document.getElementById(field.getAttribute('aria-describedby'));
      if (err) err.textContent = '';
    }

    setFormError(message) {
      if (this.formErrEl) this.formErrEl.textContent = message;
    }

    clearFormError() {
      if (this.formErrEl) this.formErrEl.textContent = '';
    }

    livraReady() {
      return !!(window.LivraCod && typeof window.LivraCod.open === 'function');
    }

    // Hand the basket to the Livra COD popup: every pack plus the cross-sell
    // lines, merged by variant.
    openLivra(packs) {
      const lines = packs.map((v) => ({ variantId: v.id, quantity: 1 }));
      this.activeXsell().forEach((it) => lines.push({ variantId: it.id, quantity: it.quantity }));
      const mergedLines = [];
      lines.forEach((ln) => {
        const hit = mergedLines.find((m) => m.variantId === ln.variantId);
        if (hit) hit.quantity += ln.quantity;
        else mergedLines.push({ variantId: ln.variantId, quantity: ln.quantity });
      });
      // Order-failure fallback (theme settings xm_order_fallback*), independent
      // of whether any WhatsApp button is shown on the page. `whatsapp` is still
      // sent in whatsapp mode for popup builds that predate `fallback`.
      const fb = this.orderFallback();
      window.LivraCod.open({
        lines: mergedLines,
        source: 'xm-quick',
        fallback: fb,
        whatsapp: fb.mode === 'whatsapp' ? fb.number : undefined,
        attribution: (typeof window.xmAttribution === 'function' ? window.xmAttribution() : undefined),
      });
    }

    // Livra is not ready at tap time. Show a loading state on the button, wait
    // for it (poll + its ready event) for about 6 s, then open it. Never fall
    // through to cart/add.js + /checkout or the native form post.
    openLivraWhenReady(packs) {
      if (this.livraWaiting) return;
      this.livraWaiting = true;
      this.submitLock = true;
      const buttons = Array.from(this.form.querySelectorAll('[data-xm-intent]'));
      buttons.forEach((b) => { b.disabled = true; });
      const label = this.addText;
      const prev = label ? label.innerHTML : null;
      if (label) label.textContent = 'CHARGEMENT…';

      const started = Date.now();
      let timer = 0;
      const finish = () => {
        window.clearInterval(timer);
        document.removeEventListener('livracod:ready', check);
        this.livraWaiting = false;
        this.submitLock = false;
        buttons.forEach((b) => { b.disabled = false; });
        if (label && prev !== null && label.textContent === 'CHARGEMENT…') label.innerHTML = prev;
      };
      const check = () => {
        if (this.livraReady()) {
          finish();
          this.openLivra(packs);
        } else if (Date.now() - started > 6000) {
          finish();
          this.setLivraMissingError();
        }
      };
      document.addEventListener('livracod:ready', check);
      timer = window.setInterval(check, 150);
    }

    // {mode, number} from the data attributes written by snippets/xm-order-fallback
    // (settings xm_order_fallback / _phone). Digits must be 8-15 or mode is 'none'.
    orderFallback() {
      const d = this.root.dataset;
      const mode = d.xmFallbackMode === 'whatsapp' || d.xmFallbackMode === 'phone' ? d.xmFallbackMode : 'none';
      const digits = (d.xmFallbackNumber || '').replace(/\D/g, '');
      if (mode === 'none' || digits.length < 8 || digits.length > 15) return { mode: 'none', number: undefined };
      return { mode, number: digits };
    }

    // The configured fallback (WhatsApp or phone), else a plain retry.
    setLivraMissingError() {
      if (!this.formErrEl) return;
      const fb = this.orderFallback();
      this.formErrEl.textContent = '';
      if (fb.mode !== 'none') {
        this.formErrEl.appendChild(
          document.createTextNode(
            fb.mode === 'phone'
              ? "Le formulaire de commande ne s'est pas chargé. Commandez par téléphone : "
              : "Le formulaire de commande ne s'est pas chargé. Commandez par WhatsApp : "
          )
        );
        const a = document.createElement('a');
        a.href = fb.mode === 'phone' ? 'tel:+' + fb.number : 'https://wa.me/' + fb.number;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = fb.mode === 'phone' ? 'appelez-nous' : 'écrivez-nous';
        this.formErrEl.appendChild(a);
        this.formErrEl.appendChild(document.createTextNode('.'));
      } else {
        this.formErrEl.textContent =
          "Le formulaire de commande ne s'est pas chargé. Vérifiez votre connexion puis réessayez.";
      }
    }

    /*
      One form, two destinations. Both buttons post the same line items; only
      `data-xm-intent` differs, and it decides whether we redirect to checkout
      or stay on the page so the customer can keep shopping the cross-sell.

      This is also the only way a multi-pack can express "one Bleu Marlin L
      and one Gris Acier XL": a product form posts ONE variant id, the cart
      Ajax API takes an `items` array. Without JavaScript the form still
      posts natively — one variant, quantity = tier — and the customer lands
      on the cart rather than on an error.
    */
    bindSubmit() {
      if (!this.form) return;

      this.form.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-xm-intent]');
        if (btn) {
          this.intent = btn.dataset.xmIntent;
          this.flushUpdate();
        }
      });

      this.form.addEventListener('submit', (e) => {
        // A colour/size change may still have its deferred half pending.
        this.flushUpdate();
        if (!this.variants) return; // no JSON, let the browser post natively

        // Round 4: guards the cross-sell modal's own buttons re-submitting
        // through requestSubmit() while a previous submit is still
        // in-flight (set below, right before the fetch; cleared in
        // .finally()). The pending-state button disable a few lines down
        // already stops a second click on the SAME button — this also
        // catches a click on the modal's buttons, which live outside the
        // form and are not in that disable list.
        if (this.submitLock) {
          e.preventDefault();
          return;
        }

        const badField = this.validateCodFields();
        if (badField) {
          e.preventDefault();
          badField.focus();
          return;
        }
        this.clearFormError();

        const packs = this.activePacks();

        // Refuse to post a basket we could not fully resolve. Posting the
        // resolved subset would charge for fewer packs than the tier states.
        if (packs.some((v) => !v || !v.available)) {
          e.preventDefault();
          this.paintPacks();
          this.setFormError(
            "Cette combinaison n'est pas disponible. Choisissez une autre taille ou un autre coloris."
          );
          const first = this.root.querySelector('[data-xm-pack-card]:not([hidden]) input:not(:disabled)');
          if (first) first.focus();
          return;
        }
        this.clearFormError();

        e.preventDefault();
        const intent = this.intent || 'buy';

        /*
          Round 4: the order CTA (.tpdp-cta--buy, intent "buy") stops here
          once — not "Ajouter au panier" — when nothing has been added from
          the cross-sell rail yet and the modal has not already shown once
          this page view. `xsell` empty is read straight off the SAME Map
          bindCrossSell() already keeps (see that method's own comment: the
          rail is a SELECTION, never an immediate cart add — AJOUTER only
          posts once this very submit handler runs, whichever button ends up
          triggering it), so "has an upsell" here means exactly what
          activeXsell() below is about to post.

          This gate has to run BEFORE both the Livra and the Releasit
          hand-offs below: it is the one thing that shows the rail at all,
          and its own CONFIRMER / Non merci buttons re-submit the form
          (see bindXsellModal()), landing back here with `xsell` now
          non-empty (or the modal already marked shown) so this branch is
          skipped the second time and the submit falls through to whichever
          hand-off applies.
        */
        if (
          this.quick &&
          intent === 'buy' &&
          this.xsellModal &&
          !this.xsellModalShown &&
          (!this.xsell || this.xsell.size === 0)
        ) {
          this.openXsellModal();
          return;
        }

        /*
          Livra COD: when the Livra COD app embed is on the page, it can take
          a mixed basket — several different pack variants plus whatever the
          cross-sell rail added — that Releasit's popup below cannot (it only
          reads the product form's single variant + quantity). So this branch
          is tried first and, when it fires, wins outright: Releasit is only
          ever reached when Livra is not installed on this theme.
        */
        if (this.quick && intent === 'buy' && this.livraReady()) {
          this.openLivra(packs);
          return;
        }

        // Quick mode never reaches Shopify checkout: if Livra has not loaded
        // yet (late or blocked in an in-app browser), wait for it on the
        // button, and say so plainly if it never comes. The Releasit branch
        // below stays reachable only where that app embed is on the page.
        if (this.quick && intent === 'buy' && !document.querySelector('#rsi_buy_now_button:not(.rsi_floating)')) {
          this.openLivraWhenReady(packs);
          return;
        }

        /*
          D62: when the Releasit COD Form embed is on the page (development
          theme only for now), the order button hands off to Releasit's
          popup instead of going to Shopify checkout. Releasit reads the
          product form's variant and quantity, so it can carry one variant
          at N units (1/2/3 packs of the same colour and size) and nothing
          else: a mixed pack, or extras picked from the rail, fall through to
          the existing checkout path below. Releasit's own two buttons are
          hidden by the quick_mode CSS and only ever clicked from here.
        */
        const rsiBtn = this.quick && intent === 'buy'
          ? document.querySelector('#rsi_buy_now_button:not(.rsi_floating)')
          : null;
        const oneVariant = packs.length > 0 && packs.every((v) => v.id === packs[0].id);
        if (rsiBtn && oneVariant && (!this.xsell || this.xsell.size === 0)) {
          let qty = this.form.querySelector('input[name="quantity"]');
          if (!qty) {
            qty = document.createElement('input');
            qty.type = 'hidden';
            qty.name = 'quantity';
            this.form.appendChild(qty);
          }
          qty.value = String(packs.length);
          const idInput = this.form.querySelector('[name="id"]');
          if (idInput) idInput.value = String(packs[0].id);
          rsiBtn.click();
          return;
        }

        const items = packs.map((v) => ({ id: v.id, quantity: 1 }));
        // Accessories chosen in the rail ride on THIS post, so they are on
        // the same order and inherit the COD properties below.
        this.activeXsell().forEach((it) => items.push(it));

        // Three line items of the same variant would collapse into one line
        // of quantity 3 anyway; merging them first keeps the cart tidy and
        // the request small.
        const merged = [];
        items.forEach((it) => {
          const hit = merged.find((m) => m.id === it.id);
          if (hit) hit.quantity += it.quantity;
          else merged.push(it);
        });

        // The three COD fields ride along as line-item properties on the
        // first line, so the order carries them into the admin.
        const props = {};
        this.form.querySelectorAll('[name^="properties["]').forEach((f) => {
          const key = f.name.slice('properties['.length, -1);
          if (f.value) props[key] = f.value;
        });
        if (Object.keys(props).length) merged[0].properties = props;

        const buttons = Array.from(this.form.querySelectorAll('[data-xm-intent]'));
        buttons.forEach((b) => { b.disabled = true; });

        // Visible pending state on the button that was actually pressed —
        // a label swap, not a spinner asset — restored in .finally() either
        // way the request lands. innerHTML, not textContent: quick_mode's
        // CTA (this.addText) holds two <span> children (paintQuickCtaLines()),
        // and a textContent round-trip would flatten them into one run of
        // text on restore. innerHTML is a no-op difference for the default
        // template's plain-text label.
        const pendingText = intent === 'cart' ? this.cartText : this.addText;
        const pendingPrev = pendingText ? pendingText.innerHTML : null;
        if (pendingText) pendingText.textContent = 'ENVOI EN COURS…';

        this.submitLock = true;

        fetch(this.cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: merged })
        })
          .then((r) => {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(() => {
            if (intent === 'buy') {
              window.location.href = this.checkoutUrl();
              return;
            }
            document.dispatchEvent(new CustomEvent('xm:cart:updated'));
            this.clearXsell();
            // The pressed cards just went back to resting, so the amount
            // they were contributing has to come back out of the totals too.
            this.paintTier(this.matchVariant());
            if (this.cartBtn && this.cartText) {
              this.cartBtn.dataset.added = 'true';
              this.cartText.textContent =
                this.cartBtn.dataset.labelAdded || this.cartText.textContent;
              window.setTimeout(() => {
                this.cartBtn.removeAttribute('data-added');
                this.cartText.textContent =
                  this.cartBtn.dataset.labelAvailable || this.cartText.textContent;
              }, 2600);
            }
          })
          .catch(() => {
            // Fall back to the native post rather than leaving the customer
            // with a button that did nothing. Validation already passed
            // above — this only runs on a network/fetch failure.
            this.form.submit();
          })
          .finally(() => {
            this.submitLock = false;
            buttons.forEach((b) => { b.disabled = false; });
            // Restore whatever the button said before "ENVOI EN COURS…",
            // unless the cart-add flipped it straight to its own "added"
            // label just above.
            if (pendingText && pendingText.textContent === 'ENVOI EN COURS…') {
              pendingText.innerHTML = pendingPrev;
            }
          });
      });
    }

    /*
      Livra COD dispatches `livracod:success` on `document` once its own
      popup places the order (contract: LivraCodApi in the cod-app repo).
      This page never sees that order — Livra owns it end to end — so all
      this does is put the pack cards and the cross-sell rail back to
      resting, the same tidy-up the existing cart/add.js "cart" path does in
      bindSubmit()'s own .then(). quick_mode only: the default template has
      neither the rail nor Livra's embed.
    */
    bindLivraSuccess() {
      if (!this.quick) return;
      document.addEventListener('livracod:success', () => {
        this.clearXsell();
        this.paintTier(this.matchVariant());
      });
    }

    /*
      Checkout, with every field this page collected already in it.

      A theme cannot create an order — that is an authenticated Admin API
      call — so the customer still lands on checkout. This makes that landing
      a single button rather than a second form: name, phone, city, country
      and a placeholder street all arrive as `checkout[...]` parameters.

      The street is a placeholder on purpose. This page asks for name, phone
      and city only; the address is taken on the WhatsApp confirmation call,
      and operations edit it onto the order afterwards. It is a merchant
      setting, and blanking that setting gives back the old behaviour —
      checkout asks the customer for it.

      Nothing here can fail loudly: if a field is empty it is left out, and if
      Shopify ever stops reading these parameters the customer arrives at the
      same checkout they arrive at today.
    */
    checkoutUrl() {
      const base = this.cartUrl('checkout');
      const cfg = this.readJson('[data-xm-prefill]');
      if (!cfg) return base;

      const field = (role) => {
        const el = this.form && this.form.querySelector('[data-xm-field="' + role + '"]');
        return el && el.value ? el.value.trim() : '';
      };

      const params = new URLSearchParams();
      const put = (key, value) => {
        if (value) params.set('checkout[' + key + ']', value);
      };

      // "Nom complet" is one field. Shopify wants two, and a last name that
      // is a repeat of the first is worse on a delivery label than an empty
      // one, so a single-word entry only fills the first.
      const name = field('name');
      if (name) {
        const cut = name.indexOf(' ');
        put('shipping_address][first_name', cut === -1 ? name : name.slice(0, cut));
        if (cut !== -1) put('shipping_address][last_name', name.slice(cut + 1).trim());
      }

      // The dial code lives beside the input, not in it: the customer types
      // 06 00 00 00 00 and the national leading zero has to come off before
      // the country code goes on, or the number dials nowhere.
      const tel = field('tel').replace(/[^0-9]/g, '');
      if (tel) {
        const dial = (cfg.dial || '').replace(/[^0-9+]/g, '');
        put('shipping_address][phone', dial ? dial + tel.replace(/^0+/, '') : tel);
      }

      put('shipping_address][city', field('city'));
      put('shipping_address][address1', cfg.address1 || '');
      put('shipping_address][zip', cfg.zip || '');
      put('shipping_address][country', cfg.country || '');

      const qs = params.toString();
      return qs ? base + '?' + qs : base;
    }

    cartUrl(path) {
      const root =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
      return root + path;
    }

    /* -------------------------------------------------------- thumbnails */

    bindThumbs() {
      const thumbs = Array.from(this.root.querySelectorAll('.xm-thumb, .tpdp-thumb[data-src]'));
      this.thumbs = thumbs;
      if (!thumbs.length || !this.heroImg) return;

      // A colour tile also selects that colourway in pack 1's colour row, so
      // the picker, the price and the order never disagree with the photo.
      // The radio's change event runs update(), which repaints the hero from
      // the variant and re-syncs the rail through syncThumbs().
      const pickColour = (value) => {
        const picker = this.root.querySelector('xm-options:not([data-xm-pack]), xm-options[data-xm-pack="1"]');
        if (!picker) return false;
        const radio = Array.from(
          picker.querySelectorAll('.xmt-opts__row:not(.xmt-opts__row--chips) input[type="radio"]')
        ).find((r) => r.value === value);
        if (!radio || radio.checked) return false;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      };

      const show = (btn) => {
        thumbs.forEach((b) => b.removeAttribute('aria-current'));
        btn.setAttribute('aria-current', 'true');
        this.swapHero(btn.dataset.src);
      };
      // The tile's own image wins over the variant's: update() paints the
      // variant image first, then show() puts the tile's back.
      this.showThumb = (btn) => {
        // update() would write the variant's image into the hero and show()
        // would then overwrite it with the tile's: one write, not two.
        this.heroHandled = true;
        try {
          if (btn.dataset.xmColour) pickColour(btn.dataset.xmColour);
        } finally {
          this.heroHandled = false;
        }
        show(btn);
      };

      // A tap on the tile that is already showing would repaint the same
      // picture and change nothing on screen (Clarity read those as dead
      // taps). It is the picture the customer is looking at and asking more
      // of, so it opens the same full-screen view the hero itself opens.
      thumbs.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-current') === 'true' && this.openLightbox) {
            this.openLightbox();
            return;
          }
          this.showThumb(btn);
        });
      });

      // Hero prev/next walk the rail in order and wrap at both ends.
      const prev = this.root.querySelector('[data-xm-hero-prev]');
      const next = this.root.querySelector('[data-xm-hero-next]');
      if (thumbs.length < 2) return;
      const step = (dir) => {
        let i = thumbs.findIndex((b) => b.getAttribute('aria-current') === 'true');
        if (i < 0) i = 0;
        this.showThumb(thumbs[(i + dir + thumbs.length) % thumbs.length]);
      };
      this.stepThumb = step;
      if (prev) {
        prev.hidden = false;
        prev.addEventListener('click', () => step(-1));
      }
      if (next) {
        next.hidden = false;
        next.addEventListener('click', () => step(1));
      }
    }

    /* --------------------------------------------------------- lightbox */

    // Full-screen view of whatever the hero shows. It never keeps its own
    // list: prev/next step the rail (so the colourway follows, exactly as
    // with the hero arrows) and the lightbox re-reads the hero afterwards.
    bindLightbox() {
      const lb = this.root.querySelector('[data-xm-lb]');
      const openBtn = this.root.querySelector('[data-xm-lb-open]');
      if (!lb || !this.heroImg || typeof lb.showModal !== 'function') return;
      const img = lb.querySelector('[data-xm-lb-img]');
      const count = lb.querySelector('[data-xm-lb-count]');
      const stage = lb.querySelector('[data-xm-lb-stage]');

      // Same Shopify file, bigger rendition. image_url carries width=N;
      // an asset_img_url carries _1600x in the name and is left as is.
      // Opens on 1600 w (a 2400 px decode is ~25-30 MB of bitmap on a phone,
      // inside the tap) and upgrades to 2400 w once the 1600 is on screen.
      let lbToken = 0;
      const paint = () => {
        lb.classList.remove('is-zoomed');
        img.style.transformOrigin = '';
        // The hero's .src (or the swap still decoding), not .currentSrc:
        // currentSrc lags a src change by a frame.
        const src = this.heroPending || this.heroImg.src;
        const token = ++lbToken;
        window.requestAnimationFrame(() => {
          if (token !== lbToken) return;
          if (!hasWidth(src)) {
            img.src = src;
            return;
          }
          img.src = atWidth(src, 1600);
          const upgrade = () => {
            if (token !== lbToken) return;
            const hi = new Image();
            hi.src = atWidth(src, 2400);
            const swap = () => { if (token === lbToken) img.src = hi.src; };
            if (hi.decode) hi.decode().then(swap, swap);
            else hi.onload = swap;
          };
          if (img.decode) img.decode().then(upgrade, upgrade);
          else img.addEventListener('load', upgrade, { once: true });
        });
        const thumbs = this.thumbs || [];
        const i = thumbs.findIndex((b) => b.getAttribute('aria-current') === 'true');
        if (count) count.textContent = thumbs.length > 1 && i >= 0 ? i + 1 + ' / ' + thumbs.length : '';
      };
      const open = () => {
        paint();
        lb.showModal();
      };
      const go = (dir) => {
        if (!this.stepThumb) return;
        this.stepThumb(dir);
        paint();
      };

      this.openLightbox = open;
      if (openBtn) {
        openBtn.hidden = false;
        openBtn.addEventListener('click', open);
      }
      this.heroImg.addEventListener('click', open);

      const prev = lb.querySelector('[data-xm-lb-prev]');
      const next = lb.querySelector('[data-xm-lb-next]');
      if (this.stepThumb) {
        if (prev) {
          prev.hidden = false;
          prev.addEventListener('click', () => go(-1));
        }
        if (next) {
          next.hidden = false;
          next.addEventListener('click', () => go(1));
        }
      }
      const rtl = document.documentElement.dir === 'rtl';
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') go(rtl ? -1 : 1);
        else if (e.key === 'ArrowLeft') go(rtl ? 1 : -1);
      });

      lb.querySelector('[data-xm-lb-close]').addEventListener('click', () => lb.close());

      // Click the picture to magnify at the point clicked; move to pan;
      // click again to come back. A click on the bare stage closes.
      const aim = (e) => {
        const r = img.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      };
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        if (lb.classList.contains('is-zoomed')) {
          lb.classList.remove('is-zoomed');
        } else {
          aim(e);
          lb.classList.add('is-zoomed');
        }
      });
      img.addEventListener('mousemove', (e) => {
        if (lb.classList.contains('is-zoomed')) aim(e);
      });
      stage.addEventListener('click', (e) => {
        if (e.target === stage) lb.close();
      });
    }

    // After a colourway change from the picker, ring the matching rail tile.
    syncThumbs(variant) {
      if (!this.thumbs || !variant || !variant.options) return;
      const tile = this.thumbs.find(
        (b) => b.dataset.xmColour && variant.options.indexOf(b.dataset.xmColour) !== -1
      );
      if (!tile) return;
      this.thumbs.forEach((b) => b.removeAttribute('aria-current'));
      tile.setAttribute('aria-current', 'true');
    }

    /* ------------------------------------------------------- size guide */

    // Opens the size-guide page as a bottom sheet when the section rendered
    // one; otherwise the link navigates, as it does without JS.
    bindSizeGuide() {
      const sheet = this.root.querySelector('[data-xm-size-guide-sheet]');
      if (!sheet || typeof sheet.showModal !== 'function') return;
      this.root.addEventListener('click', (e) => {
        const link = e.target.closest && e.target.closest('[data-xm-size-guide]');
        if (!link) return;
        e.preventDefault();
        sheet.showModal();
      });
      const close = sheet.querySelector('[data-xm-size-guide-close]');
      if (close) close.addEventListener('click', () => sheet.close());
      sheet.addEventListener('click', (e) => {
        if (e.target === sheet) sheet.close();
      });
    }

    /* -------------------------------------------------------- cross-sell */

    /*
      The rail selects accessories for THE SAME ORDER, it does not add them
      to the cart on its own. Each pressed card contributes a line to the
      `items` array the order form posts, so the accessory is on the same
      order as the tracksuit and inherits the same COD properties. Nothing is
      written to the cart until the customer submits, so a page they abandon
      leaves no orphan line behind.

      Selections live here rather than on the DOM so `activeXsell()` has one
      place to read, and so a size change re-keys cleanly.
    */
    bindCrossSell() {
      this.xsell = new Map(); // button element -> variant id

      this.root.querySelectorAll('[data-xm-xsell]').forEach((btn) => {
        const card = btn.closest('.tpdp-xsell__card') || btn.parentElement;
        const select = card ? card.querySelector('[data-xm-xsell-variant]') : null;
        const text = btn.querySelector('[data-xm-xsell-text]') || btn.querySelector('span');
        const priceEl = card ? card.querySelector('[data-xm-xsell-price]') : null;

        const paint = () => {
          const on = this.xsell.has(btn);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on) btn.dataset.added = 'true';
          else btn.removeAttribute('data-added');
          const label = on ? btn.dataset.labelAdded : btn.dataset.labelAdd;
          if (text && label) text.textContent = label;
        };

        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          if (this.xsell.has(btn)) this.xsell.delete(btn);
          else this.xsell.set(btn, Number(btn.dataset.xmXsell));
          paint();
          // Pressing or un-pressing a card changes what the order will
          // actually total, so both the tier total and the sticky price
          // have to move with it, not just on a tier or variant change.
          this.paintTier(this.matchVariant());
        });

        if (select) {
          // Colour swatches (the cap) drive the same select the size picker
          // uses, so the add button, price and pressed state below need no
          // second code path.
          const swatches = card.querySelectorAll('[data-xm-xsell-swatch]');
          const swatchName = card.querySelector('[data-xm-xsell-swatch-name]');
          const img = card.querySelector('[data-xm-xsell-img]');
          swatches.forEach((sw) => {
            sw.addEventListener('click', () => {
              if (select.value === sw.dataset.xmXsellSwatch) return;
              select.value = sw.dataset.xmXsellSwatch;
              select.dispatchEvent(new Event('change'));
            });
          });

          select.addEventListener('change', () => {
            btn.dataset.xmXsell = select.value;
            const picked = select.options[select.selectedIndex];
            swatches.forEach((sw) => {
              sw.setAttribute('aria-pressed', sw.dataset.xmXsellSwatch === select.value ? 'true' : 'false');
            });
            if (swatchName && picked) swatchName.textContent = picked.textContent;
            if (img && picked && picked.dataset.img) {
              img.removeAttribute('srcset');
              img.src = picked.dataset.img;
            }
            // The labels carry the price, so they have to follow the size —
            // these packs are one price today, which is exactly why a
            // hardcoded label would go unnoticed when one of them is not.
            const opt = select.options[select.selectedIndex];
            const money = opt && opt.dataset.price;
            if (money) {
              btn.dataset.labelAdd = 'AJOUTER (+' + money + ')';
              btn.dataset.labelAdded = 'AJOUTÉ (+' + money + ')';
              if (priceEl) priceEl.textContent = money;
            }
            // Bundle cards: the struck full price and the "bought
            // separately" line follow the size too (full price rendered by
            // Liquid in data-full; the sentence keeps its own wording).
            const full = opt && opt.dataset.full;
            if (full) {
              const fullEl = card.querySelector('[data-xm-xsell-full]');
              const sepEl = card.querySelector('[data-xm-xsell-sep]');
              if (fullEl) fullEl.textContent = full;
              if (sepEl && sepEl.dataset.tpl) sepEl.textContent = sepEl.dataset.tpl.replace('__PRICE__', full);
            }
            // Keep a pressed card pressed, pointing at the new size.
            if (this.xsell.has(btn)) this.xsell.set(btn, Number(select.value));
            paint();
            // A size change can change the price, and if the card is
            // pressed that price is already in the visible total.
            this.paintTier(this.matchVariant());
          });
        }
      });
    }

    /*
      Money, summed without reformatting it.

      Every price string this file touches — the base total, a tier pill's
      sub-label, a cross-sell card's price — was rendered once by Liquid's
      `money_without_trailing_zeros` and never touched again: that discipline
      is what stops this script printing "499.00 د.م." on load and "499 dh"
      after a click (see the file header). Summing the cross-sell rail into
      the tier total would normally mean reformatting money in JavaScript,
      which is exactly that mistake. Instead, `parseMoneyString` pulls the
      digits out of an ALREADY-FORMATTED string — currency symbol, spacing
      and decimal style included — and `formatMoney` splices a new number
      back into that same prefix/suffix, so the result carries whatever the
      shop's money format actually is without this file knowing it.
    */
    parseMoneyString(str) {
      if (typeof str !== 'string') return null;
      const cleaned = str.replace(/ /g, ' ');
      const m = cleaned.match(/\d[\d.,\s]*\d|\d/);
      if (!m) return null;
      const raw = m[0].replace(/\s/g, '');
      const lastDot = raw.lastIndexOf('.');
      const lastComma = raw.lastIndexOf(',');
      const lastSep = Math.max(lastDot, lastComma);
      let cents;
      let hasDecimals = false;
      // A trailing separator followed by exactly two digits reads as a
      // decimal mark (either "." or ","); anything else — including a
      // thousands grouping mark — is stripped as punctuation.
      if (lastSep !== -1 && raw.length - lastSep - 1 === 2) {
        hasDecimals = true;
        const intPart = raw.slice(0, lastSep).replace(/[.,]/g, '') || '0';
        const decPart = raw.slice(lastSep + 1);
        cents = parseInt(intPart, 10) * 100 + parseInt(decPart, 10);
      } else {
        cents = (parseInt(raw.replace(/[.,]/g, ''), 10) || 0) * 100;
      }
      return {
        cents: cents,
        hasDecimals: hasDecimals,
        prefix: cleaned.slice(0, m.index),
        suffix: cleaned.slice(m.index + m[0].length)
      };
    }

    // Rebuilds a money string in the same shape `parsed` was read from —
    // same currency mark, same side, same decimal style — with a new amount.
    formatMoney(parsed, cents) {
      if (!parsed) return null;
      const whole = Math.floor(cents / 100);
      const rem = cents % 100;
      const body = parsed.hasDecimals ? whole + ',' + String(rem).padStart(2, '0') : String(whole);
      return parsed.prefix + body + parsed.suffix;
    }

    // The sum, in cents, of every pressed cross-sell card's OWN displayed
    // price — the same [data-xm-xsell-price] span the select-change handler
    // below keeps in sync with the chosen size. Nothing here re-derives a
    // price from a variant id; it reads what Liquid already printed.
    xsellCents() {
      if (!this.xsell || !this.xsell.size) return 0;
      let cents = 0;
      // The bundle discount applies to ONE accessory per set (the cheapest
      // first, as Shopify's automatic discount does): the pressed bundle
      // cards beyond `this.tier` are charged their full price, not the
      // discounted one the card shows.
      const bundles = [];
      this.xsell.forEach((id, btn) => {
        const card = btn.closest('.tpdp-xsell__card') || btn.parentElement;
        const priceEl = card ? card.querySelector('[data-xm-xsell-price]') : null;
        const parsed = priceEl ? this.parseMoneyString(priceEl.textContent) : null;
        if (!parsed) return;
        const fullEl = priceEl.hasAttribute('data-xm-xsell-bundle') ? card.querySelector('[data-xm-xsell-full]') : null;
        const fullParsed = fullEl ? this.parseMoneyString(fullEl.textContent) : null;
        if (fullParsed) bundles.push({ pay: parsed.cents, full: fullParsed.cents });
        else cents += parsed.cents;
      });
      bundles.sort((a, b) => a.full - b.full);
      bundles.forEach((b, i) => {
        cents += i < (this.tier || 1) ? b.pay : b.full;
      });
      return cents;
    }

    // `baseFormatted` plus the cross-sell sum, in `baseFormatted`'s own
    // format. With nothing pressed this returns `baseFormatted` unchanged.
    withXsell(baseFormatted) {
      const extra = this.xsellCents();
      if (!extra) return baseFormatted;
      const parsed = this.parseMoneyString(baseFormatted);
      if (!parsed) return baseFormatted;
      return this.formatMoney(parsed, parsed.cents + extra) || baseFormatted;
    }

    // The accessory lines to post alongside the packs, one per pressed card.
    activeXsell() {
      if (!this.xsell) return [];
      return Array.from(this.xsell.values())
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => ({ id: id, quantity: 1 }));
    }

    // Put every pressed card back to its resting state, after the order has
    // actually been placed. Leaving them green would invite a second add.
    clearXsell() {
      if (!this.xsell) return;
      Array.from(this.xsell.keys()).forEach((btn) => {
        this.xsell.delete(btn);
        btn.setAttribute('aria-pressed', 'false');
        btn.removeAttribute('data-added');
        const text = btn.querySelector('[data-xm-xsell-text]') || btn.querySelector('span');
        if (text && btn.dataset.labelAdd) text.textContent = btn.dataset.labelAdd;
      });
    }

    /* ---------------------------------------------------------- countdown */

    /*
      Runs to a real cut-off hour, every day, and hides its whole band once
      that hour has passed rather than sitting at 00h 00m 00s. The Stitch
      mockup hard-codes "02h 34m 18s", which restarts at the same value on
      every reload — visible to any customer who refreshes, and a page that
      contradicts itself is worse than one with no clock.
    */
    bindCountdown() {
      const el = this.root.querySelector('[data-xm-cutoff]');
      if (!el) return;
      const hour = parseInt(el.dataset.xmCutoff, 10);
      if (Number.isNaN(hour)) return;

      const band = el.closest('section');
      const sub = band && band.querySelector('[data-xm-cutoff-sub]');
      const title = band && band.querySelector('[data-xm-cutoff-title]');
      /*
        Three windows (owner 2026-09-29): before the cut-off (12h) the parcel
        ships today; from then until the second cut-off (22h) it ships
        tomorrow morning; after that the clock aims at tomorrow's cut-off and
        the night sub-line says it ships tomorrow. No second cut-off set =
        the old two-window behaviour.
      */
      const secondHour = parseInt(el.dataset.xmCutoffAfter, 10);
      const pad = (n) => String(n).padStart(2, '0');

      /*
        The cut-offs are operations' hours in Morocco, so the clock runs on
        Africa/Casablanca time whatever the visitor's own time zone.
      */
      const casaNow = () => {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Africa/Casablanca', hour12: false,
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(new Date());
        const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
        // % 24 on the hour only: some engines render midnight as "24".
        return (get('hour') % 24) * 3600 + get('minute') * 60 + get('second');
      };

      const set = (node, key) => {
        if (!node) return;
        const v = node.dataset[key] || (key === 'night' ? node.dataset.after : '') || node.dataset.before;
        if (v && node.textContent !== v) node.textContent = v;
      };

      const tick = () => {
        const now = casaNow();
        const c1 = hour * 3600;
        const c2 = !Number.isNaN(secondHour) && secondHour > hour ? secondHour * 3600 : null;
        let left;
        let phase;
        if (now < c1) { left = c1 - now; phase = 'before'; }
        else if (c2 !== null && now < c2) { left = c2 - now; phase = 'after'; }
        else { left = 86400 - now + c1; phase = c2 !== null ? 'night' : 'after'; }
        set(sub, phase);
        set(title, phase === 'night' ? 'before' : phase);
        const h = Math.floor(left / 3600);
        const m = Math.floor((left % 3600) / 60);
        const sec = left % 60;
        el.textContent = pad(h) + 'h ' + pad(m) + 'm ' + pad(sec) + 's';
      };

      tick();
      this.timer = setInterval(tick, 1000);
    }

    /* ------------------------------------------------------ sticky console */

    bindSticky() {
      if (!this.sticky || !('IntersectionObserver' in window)) return;

      // quick_order (P1): visible from load, hidden whenever ANY part of the
      // order form or its own CTA is on screen, so the page never shows two
      // order buttons at once and the bar never sits over a field someone
      // is typing in. Each watched node reports separately; the bar comes
      // back only once all of them are out of view.
      if (this.sticky.dataset.xmQuick === 'true' && this.orderFormSection) {
        const watched = [this.orderFormSection, this.addBtn].filter(Boolean);
        const inView = new Set();
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) inView.add(entry.target);
            else inView.delete(entry.target);
          });
          this.sticky.dataset.visible = inView.size ? 'false' : 'true';
        });
        watched.forEach((el) => io.observe(el));
        return;
      }

      // Default path, unchanged: visible only once the real CTA is off
      // screen, so the page never shows two competing buy buttons at once.
      // (The CSS bug that keeps this bar from actually appearing on mobile
      // ships as-is here — see the quick_mode comment near the top of
      // sections/xm-main-product.liquid.)
      if (!this.addBtn) return;
      const io = new IntersectionObserver(
        ([entry]) => {
          this.sticky.dataset.visible = entry.isIntersecting ? 'false' : 'true';
        },
        { rootMargin: '0px 0px -80px 0px' }
      );
      io.observe(this.addBtn);
    }

    /* -------------------------------------------- quick-order bottom sheet */

    /*
      Tapping the sticky bar's COMMANDER, in quick_order, opens a bottom
      sheet holding the configurator (6) and the order form (9) instead of
      scrolling to them. The two sections are MOVED into the sheet
      (appendChild) and moved back to a marker comment left in their
      original spot on close — never cloned, so there is still exactly one
      set of pack radios and one set of form inputs, and every listener
      already bound above keeps working because the nodes never stop being
      the same nodes.

      Without JS, or on a browser with no <dialog>.showModal, the trigger's
      href="#xm-order-form-…" is never intercepted (no listener gets bound)
      and behaves exactly as the anchor always did — the sections are still
      in their normal place in the document because nothing has moved them.
    */
    bindSheet() {
      if (!this.sheet || !this.sheetBody || !this.sheetTrigger) return;
      if (!this.configSection || !this.orderFormSection) return;
      if (typeof this.sheet.showModal !== 'function') return;

      this.configMarker = document.createComment('xm-config-marker');
      this.formMarker = document.createComment('xm-order-form-marker');
      this.configSection.after(this.configMarker);
      this.orderFormSection.after(this.formMarker);

      // Desktop (the sheet's CSS hides it from 60rem): let the anchor scroll
      // to the inline form and put the cursor in the first field instead.
      const desktop = window.matchMedia('(min-width: 60rem)');
      this.sheetTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (!desktop.matches) {
          // Some in-app webviews expose showModal() but reject its call at
          // runtime. Do not strand the anchor after cancelling its native
          // navigation: an inline order form is always the usable fallback.
          if (this.openSheet()) return;
          this.scrollToOrder();
          return;
        }
        this.scrollToOrder();
      });

      if (this.sheetClose) {
        this.sheetClose.addEventListener('click', () => this.closeSheet());
      }

      // Backdrop tap: a click that lands on ::backdrop's box reports the
      // <dialog> element itself as e.target (::backdrop is not in the DOM
      // tree), so "the click target IS the dialog, not something inside
      // it" is the standard test for "outside the sheet's own content".
      this.sheet.addEventListener('click', (e) => {
        if (e.target === this.sheet) this.closeSheet();
      });

      // Escape is native <dialog> behaviour — the browser fires `cancel`
      // then `close` on its own. This only has to follow that close with
      // the scroll-lock and node-placement cleanup, same as a button close.
      this.sheet.addEventListener('close', () => this.onSheetClosed());
    }

    openSheet() {
      if (!this.sheet || !this.sheetBody || typeof this.sheet.showModal !== 'function') return false;
      if (this.sheet.open) return true;
      this.flushUpdate();
      // Open the dialog first (cheap: top layer, backdrop, an empty body) and
      // move the configurator and the order form in on the next frame, so the
      // tap's own task does not also pay for relocating two large subtrees.
      try {
        this.sheet.showModal();
      } catch (e) {
        // showModal() may be present but unavailable in an embedded browser.
        // Nothing has moved yet, so the inline controls are still in place.
        return false;
      }
      document.body.setAttribute('data-xm-sheet-open', 'true');
      window.requestAnimationFrame(() => {
        // Closed again before the frame: leave everything where it is.
        if (!this.sheet.open) return;
        this.sheetBody.appendChild(this.configSection);
        this.sheetBody.appendChild(this.orderFormSection);

        // Focus the name field only once colour + size are already resolved
        // (matchVariant() is non-null exactly then); otherwise land on the
        // heading, so a screen reader announces what the sheet is before
        // dropping into a field with nothing chosen for it yet.
        const ready = !!this.matchVariant();
        if (ready && this.nameEl) this.nameEl.focus();
        else if (this.sheetTitle) this.sheetTitle.focus();
      });
      return true;
    }

    // The sheet trigger remains an anchor for the no-JS path. This shared
    // JS path gives its fallback the same visible result and puts keyboard
    // users on the existing order control, never on a duplicate action.
    scrollToOrder() {
      if (this.orderFormSection && typeof this.orderFormSection.scrollIntoView === 'function') {
        this.orderFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (this.addBtn && typeof this.addBtn.focus === 'function') {
        window.setTimeout(() => this.addBtn.focus({ preventScroll: true }), 0);
      }
    }

    restoreSheetSections() {
      if (this.configMarker && this.configMarker.parentNode) {
        this.configMarker.parentNode.insertBefore(this.configSection, this.configMarker);
      }
      if (this.formMarker && this.formMarker.parentNode) {
        this.formMarker.parentNode.insertBefore(this.orderFormSection, this.formMarker);
      }
    }

    closeSheet() {
      if (!this.sheet || !this.sheet.open) return;
      this.sheet.close();
    }

    onSheetClosed() {
      // Round 4: the scroll lock is shared with the cross-sell modal (the
      // two dialogs can be open at once on mobile — this one closing must
      // not drop the lock out from under the other one).
      if (!this.xsellModal || !this.xsellModal.open) {
        document.body.removeAttribute('data-xm-sheet-open');
      }
      this.restoreSheetSections();
      if (this.sheetTrigger) this.sheetTrigger.focus();
    }

    /* -------------------------------------------- cross-sell modal (round 4) */

    /*
      AJOUTER (bindCrossSell(), above) never posts to /cart/add on its own —
      it only toggles `this.xsell` (a Map) and repaints the pressed state
      and the visible total; the actual POST happens once, here, in
      bindSubmit()'s submit handler, whichever button (the order CTA or one
      of this modal's two) ends up triggering it. So "has an upsell already"
      is read straight off that same Map (`this.xsell.size`) — there is no
      separate cart state to reconcile it against.
    */
    paintXsellModalTotal() {
      if (!this.xsellModalConfirm) return;
      const total = this.lastBaseTotal ? this.withXsell(this.lastBaseTotal) : null;
      if (total) this.xsellModalConfirm.textContent = 'CONFIRMER MA COMMANDE · ' + total;
    }

    /*
      Round 5: the price card's big price/struck/ÉCONOMIE for the SELECTED
      TIER, quick_mode + the D59 garment-claims gate only (this.showClaims —
      an accessory never gets a struck pack price, same rule the pills and
      the per-pack readback already follow). `this.lastBaseTotal` is the
      tier's CHARGED total (tier_price override already applied, computed a
      few lines above in paintTier()) — the same figure the pills and,
      before round 3, the removed tier-total card used. "Was" is computed
      here in cents (variant.price and the parsed compare_at_price string
      are both already in the shop's smallest unit) rather than reformatted
      from scratch, same discipline as withXsell()/quickTotalFormatted().

      Skips entirely when packs are mixed (this.lastBaseTotal null) —
      same call this file already makes for the sticky price two lines up
      in paintTier(): leave the price card as it was rather than show a
      number for a basket that is not one uniform price.
    */
    paintQuickPriceCard(variant) {
      // Round 6: the CTA's own "ÉCONOMISEZ <saving>" line (paintQuickCtaLines(),
      // called right after this from paintTier()) reuses whatever this
      // function just computed — reset first so a stale figure from a
      // previous variant/tier never survives a call that returns early
      // below (accessory, mixed packs, no saving this time, ...).
      this.lastQuickSaving = null;
      if (!this.quick || !this.showClaims) return;
      if (!this.lastBaseTotal) return;
      if (this.priceEl) this.priceEl.textContent = this.lastBaseTotal;

      const totalParsed = this.parseMoneyString(this.lastBaseTotal);
      if (!totalParsed) return;

      let wasCents = null;
      if (variant && variant.compare_at_price_formatted) {
        const parsedWas = this.parseMoneyString(variant.compare_at_price_formatted);
        if (parsedWas) wasCents = parsedWas.cents * this.tier;
      } else if (variant && this.tier > 1) {
        wasCents = variant.price * this.tier;
      }

      if (wasCents !== null && wasCents > totalParsed.cents) {
        if (this.wasEl) {
          this.wasEl.textContent = this.formatMoney(totalParsed, wasCents);
          show(this.wasEl, true);
        }
        const savingFormatted = this.formatMoney(totalParsed, wasCents - totalParsed.cents);
        this.lastQuickSaving = savingFormatted;
        if (this.savingEl) {
          if (this.savingTextEl) this.savingTextEl.textContent = 'ÉCONOMIE : ' + savingFormatted;
          show(this.savingEl, true);
        }
      } else {
        if (this.wasEl) show(this.wasEl, false);
        if (this.savingEl) show(this.savingEl, false);
      }
    }

    /*
      The order CTA in quick_mode — "COMMANDER MAINTENANT — ÉCONOMISEZ
      <saving>" on one line (was two until 2026-09-29), using the SAME figure
      paintQuickPriceCard() just computed for the price card's own ÉCONOMIE
      badge (this.lastQuickSaving), so the two numbers can never disagree.
      Rebuilds data-xm-add-text's two <span> children fresh each time
      rather than patching text in place — cheap (two nodes) and avoids
      tracking which line existed last.

      Skipped while the button is disabled: update()'s own 'INDISPONIBLE'/
      'ENVOI EN COURS…' text further up already says the state that
      matters more, and this would otherwise stomp on it every time
      paintTier() runs after that text is set.
    */
    paintQuickCtaLines() {
      if (!this.quick || !this.addText) return;
      if (this.addBtn && this.addBtn.disabled) return;
      this.addText.textContent = '';
      const line1 = document.createElement('span');
      line1.className = 'tpdp-cta__line1';
      // One line since 2026-09-29 (owner): "COMMANDER MAINTENANT — ÉCONOMISEZ
      // <saving>", or just "COMMANDER MAINTENANT" when there is no saving.
      // Cash on delivery is still said under the button.
      line1.textContent = 'COMMANDER MAINTENANT' + (this.lastQuickSaving ? ' — ÉCONOMISEZ ' + this.lastQuickSaving : '');
      this.addText.appendChild(line1);
    }

    bindXsellModal() {
      if (!this.xsellModal || !this.xsellRailSection) return;
      if (typeof this.xsellModal.showModal !== 'function') return;

      this.xsellModalBody = this.xsellModal.querySelector('[data-xm-xsell-modal-body]');
      this.xsellModalTitle = this.xsellModal.querySelector('[data-xm-xsell-modal-title]');
      this.xsellModalConfirm = this.xsellModal.querySelector('[data-xm-xsell-modal-confirm]');
      this.xsellModalSkip = this.xsellModal.querySelector('[data-xm-xsell-modal-skip]');
      this.xsellModalClose = this.xsellModal.querySelector('[data-xm-xsell-modal-close]');
      if (!this.xsellModalBody) return;

      this.xsellRailMarker = document.createComment('xm-xsell-rail-marker');
      this.xsellRailSection.after(this.xsellRailMarker);

      // Primary and "Non merci" both run the SAME submit path the order CTA
      // uses — same validation (already passed, this modal only opens after
      // it did), same line items, same properties, now including whatever
      // was added in this modal. requestSubmit() re-fires the exact submit
      // handler above; xsellModalShown is already true by the time this
      // dialog exists to be clicked in, so the gate that opened it does not
      // reopen it.
      const confirmAndSubmit = (btn) => {
        if (!btn || btn.disabled) return;
        if (this.xsellModalConfirm) this.xsellModalConfirm.disabled = true;
        if (this.xsellModalSkip) this.xsellModalSkip.disabled = true;
        this.closeXsellModal();
        this.intent = 'buy';
        // Safari < 16 has no requestSubmit(); a dispatched submit event still
        // runs the handler above, where form.submit() would bypass it.
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      };

      if (this.xsellModalConfirm) {
        this.xsellModalConfirm.addEventListener('click', () => confirmAndSubmit(this.xsellModalConfirm));
      }
      if (this.xsellModalSkip) {
        this.xsellModalSkip.addEventListener('click', () => confirmAndSubmit(this.xsellModalSkip));
      }
      if (this.xsellModalClose) {
        this.xsellModalClose.addEventListener('click', () => this.closeXsellModal());
      }
      // Same backdrop-tap test as the bottom sheet: a click that lands on
      // ::backdrop reports the <dialog> element itself as e.target.
      this.xsellModal.addEventListener('click', (e) => {
        if (e.target === this.xsellModal) this.closeXsellModal();
      });
      // Escape is native <dialog> behaviour (cancel -> close); this only
      // has to follow it with the scroll-lock/node-placement cleanup below,
      // same as a close-button click — "just closes, returns to the form,
      // no submit" either way.
      this.xsellModal.addEventListener('close', () => this.onXsellModalClosed());
    }

    openXsellModal() {
      if (!this.xsellModal || this.xsellModal.open) return;
      this.xsellModalShown = true;
      this.xsellModalBody.appendChild(this.xsellRailSection);
      this.xsellModal.showModal();
      document.body.setAttribute('data-xm-sheet-open', 'true');
      if (this.xsellModalTitle) this.xsellModalTitle.focus();
    }

    closeXsellModal() {
      if (!this.xsellModal || !this.xsellModal.open) return;
      this.xsellModal.close();
    }

    onXsellModalClosed() {
      // Shared scroll lock with the bottom sheet — see onSheetClosed()'s
      // mirror image of this same check.
      if (!this.sheet || !this.sheet.open) {
        document.body.removeAttribute('data-xm-sheet-open');
      }
      if (this.xsellRailMarker && this.xsellRailMarker.parentNode) {
        this.xsellRailMarker.parentNode.insertBefore(this.xsellRailSection, this.xsellRailMarker);
      }
      if (this.xsellModalConfirm) this.xsellModalConfirm.disabled = false;
      if (this.xsellModalSkip) this.xsellModalSkip.disabled = false;
    }
  }

  const init = () => {
    document.querySelectorAll('product-component').forEach((el) => {
      if (el.dataset.xmInit) return;
      el.dataset.xmInit = '1';
      new XmProduct(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
